import {
    REGILY_COUNTRY,
    REGILY_LANGUAGE,
    MARKETS,
    REGILY_REMAINING_COUNTRIES_MAP,
    REGILY_COUNTRY_REV,
    RESTRICTED_PROVINCE_MARKET_MAP,
    QUERY_PARAM_NAMES,
    WO_TYPES,
    NEW_REGISTRATION_ENABLED_MARKETS
} from './constants';
import { pageShellQuery, welcomeOffersQuery } from './queries';
import type {
    TContentfulModule,
    TGraphqlBody,
    TLosPageShellResponse,
    TLosWelcomeOffersResponse,
    TPreferredWelcomeOffer
} from './types';
import { browser } from '$app/environment';
import { Cookies, fromDocument } from '$lib/services/helpers/cookies';
import { PUBLIC_DIRECTUS_TEST_URL, PUBLIC_DIRECTUS_URL } from '$env/static/public';
import type { TMarket } from '$lib/services/market/types';
import { getEnvironmentHost, isTest } from '$lib/services/environment';

const getLosGraphqlEndpoint = (url: string) => `${getEnvironmentHost(url)}/api/los/graphql`;
const getRegilyAssetsPath = (url: string) => `${getEnvironmentHost(url)}/casumo-regily`;
const getManifestPath = (url: string) => `${getRegilyAssetsPath(url)}/manifest.json`;

const losGraphqlFetch = <T>(
    body: TGraphqlBody,
    fetch: typeof globalThis.fetch,
    sourceWo: string | null,
    url: string
): Promise<T> => {
    return fetch(getLosGraphqlEndpoint(url), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'source-wo': sourceWo || ''
        },
        body: JSON.stringify(body)
    }).then((response) => {
        return response.json();
    });
};

export const fetchRegilyModule = async (
    variables: { [key: string]: string },
    fetch: typeof globalThis.fetch,
    sourceWo: string | null,
    url: string
) => {
    const pageShellResponse: TLosPageShellResponse = await losGraphqlFetch<TLosPageShellResponse>(
        {
            query: pageShellQuery,
            variables
        },
        fetch,
        sourceWo,
        url
    );

    return pageShellResponse.data.pageShell?.modules?.find(
        (module: TContentfulModule) => module.moduleName == 'ModuleRegily'
    );
};

export const fetchWelcomeOffers = async (
    variables: { offers: string[]; lang: string },
    fetch: typeof globalThis.fetch,
    sourceWo: string | null,
    url: string
) => {
    return await losGraphqlFetch<TLosWelcomeOffersResponse>(
        { query: welcomeOffersQuery, variables },
        fetch,
        sourceWo,
        url
    );
};

export const fetchBundle = async (url: string) => {
    try {
        const manifest: { bundle: string } = await fetch(getManifestPath(url)).then((res) =>
            res.json()
        );

        return `${getRegilyAssetsPath(url)}/${manifest.bundle}`;
    } catch (e) {
        return {};
    }
};

const getWelcomeOffer = (
    preferredOfferId: string,
    welcomeOffersExtended: TLosWelcomeOffersResponse['data']['welcomeOffersExtended']
) => {
    if (!preferredOfferId) {
        console.warn(
            `Calling getWelcomeOffer but WO is '${preferredOfferId}' ${JSON.stringify(
                welcomeOffersExtended
            )}`
        );
    }

    return preferredOfferId;
};
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const getPreferredOffer = (welcomeOffersExtended: any) => {
    const urlParams = new URLSearchParams(window?.location?.search);
    const preferredOfferFromQuery = (urlParams.get(QUERY_PARAM_NAMES.PREFERRED_OFFER) ||
        WO_TYPES.CASINO) as TPreferredWelcomeOffer;
    const preferredOffer = welcomeOffersExtended?.offers?.find(
        (offer: { bonusType: string }) => offer.bonusType === preferredOfferFromQuery
    );

    return preferredOffer ? preferredOfferFromQuery : WO_TYPES.CASINO;
};

export const getRegilyConfig = (
    market: TMarket,
    regilyModule: TContentfulModule | undefined,
    welcomeOffersExtended: TLosWelcomeOffersResponse['data']['welcomeOffersExtended'],
    trackingId: string,
    regilyEnv: string,
    preferredWelcomeOffer: TPreferredWelcomeOffer,
    preferredOfferId: string
) => {
    const overviewBottomBanner =
        preferredWelcomeOffer === 'odds'
            ? regilyModule?.props.welcomeOffersConfig.overviewBottomBannerSports
            : regilyModule?.props.welcomeOffersConfig.overviewBottomBanner;
    return {
        lang: REGILY_LANGUAGE[market.slug],
        country: REGILY_COUNTRY[market.slug],
        countryCodeByIp: null, // This prop is not used in regily.
        gtmTracking: true,
        pageId: browser ? location.pathname : '',
        env: regilyEnv,
        mode: regilyModule?.props.mode,
        dev: false,
        variant: regilyModule?.props.variant,
        depositLater: true,
        setFingerprintV2: null,
        displayBonusSelector: regilyModule?.props.welcomeOffersConfig.displayBonusSelector,
        shouldShowNewDesign: false,
        logger: {
            debug: console.debug,
            info: console.info,
            warn: console.warn,
            error: console.error
        },
        getWelcomeOffer: () => {
            // This function supposed to return the selcted welcome offer id.
            return getWelcomeOffer(preferredOfferId, welcomeOffersExtended);
        },
        welcomeOffersExtended,
        preferredOffer: preferredWelcomeOffer,
        preselectedWelcomeOfferId: preferredOfferId, // welcome offer id
        trackingId,
        iovationEnabled: false, // config.regily.iovationEnabled
        overviewBottomBanner: { text: overviewBottomBanner }
    };
};

export const isCountryOrLastStep = (key: string) => {
    return (
        key === 'user.country' || key === 'consent.terms.consent' || key === 'credentials.consent'
    );
};

const redirect = (href: string) => {
    window.location.href = href;
};

export const redirectPlayer = (market: string, newMarket: string, selectedCountry: string) => {
    if (newMarket && market !== newMarket) {
        if (selectedCountry === REGILY_COUNTRY[MARKETS.SV]) {
            if (browser) {
                redirect(
                    `${window?.location?.origin}/${MARKETS.SV}/${window?.location?.search}#playnow`
                );
            }
        } else {
            if (browser) {
                const isStandaloneEnabledMarket =
                    NEW_REGISTRATION_ENABLED_MARKETS.includes(newMarket);
                const href = isStandaloneEnabledMarket
                    ? `${window?.location?.origin}/${newMarket}/register/${window?.location?.search}#signup`
                    : `${window?.location?.origin}/${newMarket}/${window?.location?.search}#signup`;
                redirect(href);
            }
        }
    }
};
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const regilyRedirectionLogic = (marketObj: TMarket, key: string, data: any) => {
    const market = marketObj.slug;
    
    if (market !== MARKETS.SV && isCountryOrLastStep(key) && data.user && data.user.country) {
        const selectedCountry = data.user.country?.toLowerCase() as string;
        // Equivalent LOS market of the selectedCountry
        const newMarket =
            REGILY_COUNTRY_REV[selectedCountry] ||
            REGILY_REMAINING_COUNTRIES_MAP[selectedCountry] ||
            MARKETS.EN;

        // We don't change market if user from Ontario market choose Canada Country
        // so we can skip the redirection and check restrictedMarket later.
        if (RESTRICTED_PROVINCE_MARKET_MAP[market.toUpperCase()] && newMarket === MARKETS.ENCA) {
            return;
        }

        redirectPlayer(market, newMarket, selectedCountry);
    }
    // If we are on a restricted Canadian market or ENCA market
    // then we have to redirect to the correct market based on the province.
    if (
        data?.user?.province &&
        data?.user?.country?.toLowerCase() === REGILY_COUNTRY[MARKETS.ENCA]
    ) {
        const restrictedMarket = RESTRICTED_PROVINCE_MARKET_MAP[data.user.province];

        // If forexample ontario player on "/en-ca" market.
        if (restrictedMarket && restrictedMarket !== market) {
            const cookies = new Cookies(fromDocument(document));
            cookies.set('geoRegionOverride', MARKETS.ON);
            if (browser) {
                redirect(
                    `${window?.location?.origin}/${restrictedMarket}/register/${window?.location?.search}#signup`
                );
            }
        }
        // If for example canada player is on "/on" market.
        if (!restrictedMarket && market !== MARKETS.ENCA) {
            const cookies = new Cookies(fromDocument(document));
            cookies.set('geoRegionOverride', MARKETS.ENCA);
            if (browser) {
                redirect(
                    `${window?.location?.origin}/${MARKETS.ENCA}/register/${window?.location?.search}#signup`
                );
            }
        }
    }
};

export const addBonusTermsLinksToOffers = (
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    welcomeOffersExtended: { offers: { assetId: string; bonusTermsLink: any }[] },
    bonusTermsLinks: { offer: string; bonusTermsLink: string }[]
) => {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    welcomeOffersExtended?.offers?.forEach((wo: { assetId: string; bonusTermsLink: any }) => {
        const termLink = bonusTermsLinks?.find(
            (btl: { offer: string }) => btl?.offer === wo?.assetId
        );
        wo.bonusTermsLink = termLink?.bonusTermsLink || '';
    });
};

export const extractNativeEventDataFromCookie = (cookie: string | undefined | null) => {
    if (cookie) {
        const parts = cookie.split(',');

        const result = {
            playerId: parts[0]?.trim(),
            welcomeOfferId: parts[1]?.trim()
        };
        return result;
    }
    return null;
};

export const deleteCookie = (cookieName: string) => {
    document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
};

export const getDirectusHost = (url: string) => {
    if (isTest(url)) {
        return PUBLIC_DIRECTUS_TEST_URL;
    }
    return PUBLIC_DIRECTUS_URL;
};
